import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import axiosInstance from '../configuration/Request';
import { showSuccessToast, showErrorToast } from '../Components/Toaster';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../assets/components/Popup/NewProjectModal.scss';


class NewProjectModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'geneList',
      geneInputType: 'multiselect', // 'multiselect' or 'file'
      selectedGenes: [],
      geneListFile: null,
      geneListError: '',
      networkFile: null,
      networkFileError: '',
      isDropdownOpen: false,
      searchTerm: '',
      isSubmitting: false,
      geneOptions: [], // Will hold gene options from API
      isLoadingGenes: false, // Loading state for gene options
      sampleFiles: [], // Will hold sample files from API
    };
    this.searchContainerRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    this.fetchGeneOptions(); // Fetch gene options when component mounts
    this.fetchSampleFiles(); // Fetch sample files when component mounts
  }

  fetchGeneOptions = async () => {
    this.setState({ isLoadingGenes: true });
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_URL}api/all-unique-gene`
      );

      // Map API response to dropdown format
      if (response?.data?.data?.attributes?.unique_genes) {
        const geneOptions = response.data.data.attributes.unique_genes.map(gene => ({
          value: gene,
          label: gene
        }));
        this.setState({ geneOptions, isLoadingGenes: false });
      } else {
        this.setState({ isLoadingGenes: false });
        console.error('Unexpected API response format:', response);
      }
    } catch (error) {
      console.error('Error fetching gene options:', error);
      this.setState({ isLoadingGenes: false });
      showErrorToast('Failed to load gene options. Please try again.');
    }
  };

  fetchSampleFiles = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_URL}api/sample-files`
      );
      if (response?.data?.data) {
        this.setState({ sampleFiles: response.data.data });
      }
    } catch (error) {
      console.error('Error fetching sample files:', error);
    }
  };

  handleDownloadSample = () => {
    const { activeTab, sampleFiles } = this.state;
    // Find relevant sample file based on active tab
    // Tab 'geneList' -> genelistsample.csv
    // Tab 'networkFile' -> networksample.csv
    const targetName = activeTab === 'geneList' ? 'genelistsample.csv' : 'networksample.csv';
    const sample = sampleFiles.find(f => f.name === targetName);

    if (sample && sample.url) {
      window.open(sample.url, '_blank');
    } else {
      showErrorToast(`Sample file for ${activeTab === 'geneList' ? 'Gene List' : 'Network File'} not found.`);
    }
  };

  componentDidUpdate(prevProps) {
    // Reset form when modal closes
    if (prevProps.isOpen && !this.props.isOpen) {
      this.resetForm();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (
      this.searchContainerRef.current &&
      !this.searchContainerRef.current.contains(event.target)
    ) {
      this.setState({ isDropdownOpen: false });
    }
  };

  resetForm = () => {
    this.setState({
      activeTab: 'geneList',
      geneInputType: 'multiselect',
      selectedGenes: [],
      geneListFile: null,
      geneListError: '',
      networkFile: null,
      networkFileError: '',
      searchTerm: '',
      isDropdownOpen: false,
    });
  };

  validateFile = (file, maxSizeMB = 1) => {
    const maxSize = maxSizeMB * 1024 * 1024;

    if (!file) return { valid: false, error: 'No file selected' };

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['txt', 'csv'].includes(fileExtension)) {
      return { valid: false, error: 'Invalid format. Only .txt and .csv files are allowed.' };
    }

    if (file.size > maxSize) {
      return { valid: false, error: `File size exceeds ${maxSizeMB}MB limit.` };
    }

    if (file.size === 0) {
      return { valid: false, error: 'File is empty.' };
    }

    return { valid: true, error: '' };
  };

  handleGeneListFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = this.validateFile(file);
    if (validation.valid) {
      this.setState({ geneListFile: file, geneListError: '' });
    } else {
      this.setState({ geneListFile: null, geneListError: validation.error });
    }
  };

  handleNetworkFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = this.validateFile(file);
    if (validation.valid) {
      this.setState({ networkFile: file, networkFileError: '' });
    } else {
      this.setState({ networkFile: null, networkFileError: validation.error });
    }
  };

  isFormValid = () => {
    const { activeTab, geneInputType, selectedGenes, geneListFile, geneListError, networkFile, networkFileError } = this.state;

    if (activeTab === 'geneList') {
      if (geneInputType === 'multiselect') {
        return selectedGenes.length > 0;
      } else {
        return geneListFile && !geneListError;
      }
    } else if (activeTab === 'networkFile') {
      return networkFile && !networkFileError;
    }
    return false;
  };

  removeGene = (geneValue) => {
    const { selectedGenes } = this.state;
    this.setState({ selectedGenes: selectedGenes.filter(g => g.value !== geneValue) });
  };

  removeFile = (e, type) => {
    e.stopPropagation(); // Prevent opening file dialog
    if (type === 'gene') {
      this.setState({ geneListFile: null, geneListError: '' });
      const input = document.getElementById('geneListFile');
      if (input) input.value = '';
    } else if (type === 'network') {
      this.setState({ networkFile: null, networkFileError: '' });
      const input = document.getElementById('networkFile');
      if (input) input.value = '';
    }
  };

  handleSubmit = async () => {
    if (!this.isFormValid()) return;

    const { activeTab, geneInputType, selectedGenes, geneListFile, networkFile } = this.state;

    this.setState({ isSubmitting: true });

    try {
      let response;

      if (activeTab === 'geneList') {
        if (geneInputType === 'multiselect' && selectedGenes.length > 0) {
          // Scenario 1: Gene List Dropdown - JSON payload
          const payload = {
            data: {
              gene_text: selectedGenes.map(g => g.value)
            }
          };

          response = await axiosInstance.post(
            `${process.env.REACT_APP_BASE_URL}api/gene-lists`,
            payload,
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );
        } else if (geneInputType === 'file' && geneListFile) {
          // Scenario 2: Gene List File Upload - FormData
          const formData = new FormData();
          formData.append('files.gene_file', geneListFile);
          formData.append('file_type', 'gene');

          response = await axiosInstance.post(
            `${process.env.REACT_APP_BASE_URL}api/gene-lists`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
        }
      } else if (activeTab === 'networkFile' && networkFile) {
        // Scenario 3: Network File Upload - FormData
        const formData = new FormData();
        formData.append('files.gene_file', networkFile);
        formData.append('file_type', 'network');

        response = await axiosInstance.post(
          `${process.env.REACT_APP_BASE_URL}api/gene-lists`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      // Success
      showSuccessToast('Successfully Project is Created');

      // Call onSuccess callback if provided
      if (this.props.onSuccess && response) {
        this.props.onSuccess(response.data);
      }

      // Close modal and reset form
      this.handleClose();

    } catch (error) {
      // Error handling
      console.error('API Error:', error);

      if (error?.response?.data?.error?.message) {
        showErrorToast(error.response.data.error.message);
      } else if (error?.message) {
        showErrorToast(error.message);
      } else {
        showErrorToast('Failed to create project. Please try again.');
      }
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  handleClose = () => {
    this.resetForm();
    if (this.props.toggle) {
      this.props.toggle();
    }
  };

  handleGeneInputTypeChange = (type) => {
    this.setState({
      geneInputType: type,
      // Clear the other option when switching
      selectedGenes: type === 'file' ? [] : this.state.selectedGenes,
      geneListFile: type === 'multiselect' ? null : this.state.geneListFile,
      geneListError: '',
      searchTerm: '',
      isDropdownOpen: false,
    });
  };

  render() {
    const {
      activeTab,
      geneInputType,
      selectedGenes,
      geneListFile,
      geneListError,
      networkFile,
      networkFileError,
      isDropdownOpen,
      searchTerm,
      geneOptions,
      isLoadingGenes,
    } = this.state;

    // Filter genes that start with the search term (like AutoSuggestSearch)
    // This is more efficient for large datasets (40,000+ items)
    const filteredGenes = searchTerm
      ? geneOptions.filter(gene =>
        gene.value.toLowerCase().startsWith(searchTerm.toLowerCase())
      )
      : [];

    return (
      <Modal isOpen={this.props.isOpen} toggle={this.handleClose} className="new-project-modal" size="lg">
        <ToastContainer />
        <ModalHeader toggle={this.handleClose}>
          Create New Project
        </ModalHeader>

        <ModalBody>
          <div className="npm-tab-container">
            <button
              className={`npm-tab ${activeTab === 'geneList' ? 'npm-tab-active' : ''}`}
              onClick={() => this.setState({ activeTab: 'geneList' })}
            >
              Gene List
            </button>
            <button
              className={`npm-tab ${activeTab === 'networkFile' ? 'npm-tab-active' : ''}`}
              onClick={() => this.setState({ activeTab: 'networkFile' })}
            >
              Network File
            </button>
          </div>

          <div className="npm-tab-content">
            {activeTab === 'geneList' && (
              <div className="npm-gene-list-section">
                <p className="npm-section-hint">Choose one input method</p>

                {/* Radio Button Section */}
                <div className="npm-form-section">
                  <div className="npm-radio-option">
                    <input
                      type="radio"
                      id="gene-multiselect"
                      name="geneInputType"
                      value="multiselect"
                      checked={geneInputType === 'multiselect'}
                      onChange={() => this.handleGeneInputTypeChange('multiselect')}
                      className="npm-radio-input"
                    />
                    <label htmlFor="gene-multiselect" className="npm-radio-label">
                      Gene List (Multi-select)
                    </label>
                  </div>

                  {/* Multi-select Section - Only show when selected */}
                  {geneInputType === 'multiselect' && (
                    <div className="npm-input-section">
                      <p className="npm-input-hint">Start typing to search and select 1–20 genes from the list.</p>
                      {/* Tags Display */}
                      {selectedGenes.length > 0 && (
                        <div className="npm-tags-container">
                          {selectedGenes.map((gene, index) => (
                            <div key={index} className="npm-tag">
                              {gene.value}
                              <span
                                className="npm-tag-close"
                                onClick={() => this.removeGene(gene.value)}
                              >
                                ✕
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Search Input with Autocomplete */}
                      <div className="npm-search-container" ref={this.searchContainerRef}>
                        <div className="npm-search-input-wrapper">
                          <input
                            type="text"
                            className="npm-search-input"
                            placeholder={isLoadingGenes ? "Loading genes..." : ""}
                            value={searchTerm}
                            onChange={(e) => this.setState({ searchTerm: e.target.value, isDropdownOpen: e.target.value.length > 0 })}
                            onFocus={() => searchTerm && this.setState({ isDropdownOpen: true })}
                            disabled={selectedGenes.length >= 20 || isLoadingGenes}
                          />
                          <div className="npm-dropdown-icon">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </div>
                        </div>

                        {isDropdownOpen && searchTerm && (
                          <div className="npm-suggestions-box">
                            {isLoadingGenes ? (
                              <div className="npm-no-results">Loading genes...</div>
                            ) : filteredGenes.length === 0 ? (
                              <div className="npm-no-results">No genes found</div>
                            ) : (
                              filteredGenes.map(gene => (
                                <div
                                  key={gene.value}
                                  className={`npm-suggestion ${selectedGenes.find(g => g.value === gene.value) ? 'npm-suggestion-selected' : ''}`}
                                  onClick={() => {
                                    if (selectedGenes.length >= 20) {
                                      this.setState({
                                        geneListError: 'Maximum 20 genes allowed. Please remove some genes to add more.',
                                        isDropdownOpen: false,
                                        searchTerm: ''
                                      });
                                      return;
                                    }
                                    if (!selectedGenes.find(g => g.value === gene.value)) {
                                      this.setState({
                                        selectedGenes: [...selectedGenes, gene],
                                        searchTerm: '',
                                        isDropdownOpen: false,
                                        geneListError: ''
                                      });
                                    }
                                  }}
                                >
                                  <span>{gene.label}</span>
                                  {selectedGenes.find(g => g.value === gene.value) && (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="npm-check-icon">
                                      <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>

                      {selectedGenes.length > 0 && (
                        <div className="npm-selection-count">
                          {selectedGenes.length} of 20 gene(s) selected
                        </div>
                      )}
                      {selectedGenes.length >= 20 && (
                        <div className="npm-info-message">
                          Maximum limit reached. Remove genes to add different ones.
                        </div>
                      )}
                      {geneListError && <div className="npm-error-message">{geneListError}</div>}
                    </div>
                  )}
                </div>

                <div className="npm-form-section">
                  <div className="npm-radio-option">
                    <input
                      type="radio"
                      id="gene-file"
                      name="geneInputType"
                      value="file"
                      checked={geneInputType === 'file'}
                      onChange={() => this.handleGeneInputTypeChange('file')}
                      className="npm-radio-input"
                    />
                    <label htmlFor="gene-file" className="npm-radio-label">
                      Upload Gene List File
                    </label>
                  </div>

                  {/* File Upload Section - Only show when selected */}
                  {geneInputType === 'file' && (
                    <div className="npm-input-section">
                      <p className="npm-file-formats">Supported formats: .txt, .csv • Maximum size 1 MB</p>
                      <div className="npm-file-upload-card" onClick={() => document.getElementById('geneListFile').click()}>
                        <input
                          type="file"
                          id="geneListFile"
                          className="npm-file-input"
                          accept=".txt,.csv"
                          onChange={this.handleGeneListFileChange}
                          style={{ display: 'none' }}
                        />
                        <div className="npm-upload-icon-wrapper">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="17 8 12 3 7 8"></polyline>
                            <line x1="12" y1="3" x2="12" y2="15"></line>
                          </svg>
                        </div>
                        <div className="npm-upload-text">
                          <div className="npm-upload-title">Choose file</div>
                          <div className="npm-upload-subtitle">Drop your file here or browse from your computer</div>
                        </div>
                        <div className="npm-file-status">
                          {geneListFile && (
                            <div className="npm-file-pill">
                              <span className="npm-file-pill-text">{geneListFile.name}</span>
                              <span
                                className="npm-file-pill-close"
                                onClick={(e) => this.removeFile(e, 'gene')}
                              >
                                ✕
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="npm-upload-footer">
                        <p className="npm-footer-tip">For best results, use the recommended template with gene IDs in the first column.</p>
                        <button className="npm-download-sample-btn" onClick={this.handleDownloadSample}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                          Download sample file format
                        </button>
                      </div>
                      {geneListError && <div className="npm-error-message">{geneListError}</div>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'networkFile' && (
              <div className="npm-network-file-section">
                <p className="npm-section-hint">Network file is required (source-target pairs)</p>

                <div className="npm-form-section">
                  <p className="npm-file-formats">Supported formats: .txt, .csv • Maximum size 1 MB</p>
                  <div className="npm-file-upload-card" onClick={() => document.getElementById('networkFile').click()}>
                    <input
                      type="file"
                      id="networkFile"
                      className="npm-file-input"
                      accept=".txt,.csv"
                      onChange={this.handleNetworkFileChange}
                      style={{ display: 'none' }}
                    />
                    <div className="npm-upload-icon-wrapper">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                    </div>
                    <div className="npm-upload-text">
                      <div className="npm-upload-title">Choose file</div>
                      <div className="npm-upload-subtitle">Drop your file here or browse from your computer</div>
                    </div>
                    <div className="npm-file-status">
                      {networkFile && (
                        <div className="npm-file-pill">
                          <span className="npm-file-pill-text">{networkFile.name}</span>
                          <span
                            className="npm-file-pill-close"
                            onClick={(e) => this.removeFile(e, 'network')}
                          >
                            ✕
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="npm-upload-footer">
                    <p className="npm-footer-tip">For best results, use the recommended template with source-target pairs.</p>
                    <button className="npm-download-sample-btn" onClick={this.handleDownloadSample}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download sample file format
                    </button>
                  </div>
                  {networkFileError && <div className="npm-error-message">{networkFileError}</div>}
                </div>
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={this.handleClose} disabled={this.state.isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={this.handleSubmit}
            disabled={!this.isFormValid() || this.state.isSubmitting}
          >
            {this.state.isSubmitting ? 'Creating...' : 'Create Project'}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

NewProjectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default NewProjectModal;
